import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import ConfigCard from './ConfigCard';
import { ChipsBox, ChipToggle, AvailableChip, SubLabel } from './_PdiHelpers';

const LANGS = [
    { code: 'nl', label: 'Dutch'   },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'French'  },
];

// Two ordered groups + an "available" pool. The schema splits on-page text
// fields from meta/add-on fields, since they're rendered in different blocks
// on the storefront.
const ALL_FIELDS = [
    { key: 'shortDescription',    i18nKey: 'cfg_pdi_dtl_short',          fallback: 'Short description'    },
    { key: 'longDescription',     i18nKey: 'cfg_pdi_dtl_long',           fallback: 'Long description'     },
    { key: 'marketingCopy',       i18nKey: 'cfg_pdi_dtl_marketing',      fallback: 'Marketing copy'       },
    { key: 'washInstructions',    i18nKey: 'cfg_pdi_dtl_wash',           fallback: 'Wash instructions'    },
    { key: 'seoDescription',      i18nKey: 'cfg_pdi_dtl_seo',            fallback: 'SEO description'      },
    { key: 'materialComposition', i18nKey: 'cfg_pdi_dtl_material_comp',  fallback: 'Material composition' },
    { key: 'careLabel',           i18nKey: 'cfg_pdi_dtl_care_label',     fallback: 'Care label'           },
    { key: 'origin',              i18nKey: 'cfg_pdi_dtl_origin',         fallback: 'Origin'               },
    { key: 'fitNotes',            i18nKey: 'cfg_pdi_dtl_fit_notes',      fallback: 'Fit notes'            },
];

// Default placement: which fields land in which group when nothing has been
// configured yet. Anything not listed defaults to AVAILABLE.
const DEFAULT_PLACEMENT = {
    shortDescription: 'page',
    longDescription:  'page',
    marketingCopy:    'page',
    washInstructions: 'meta',
};

const DescriptionTextsPerLanguageCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const [tab, setTab] = useState(0);
    const lang = LANGS[tab].code;
    const langData = value[lang] || {};

    // Each field stores its placement: 'page' | 'meta' | 'available'.
    const placementOf = (k) => langData[k]?.placement || DEFAULT_PLACEMENT[k] || 'available';
    const setPlacement = (k, placement) => {
        if (!onChange) return;
        onChange(lang, { ...langData, [k]: { placement } });
    };

    const buildItems = (placement) => ALL_FIELDS.filter(f => placementOf(f.key) === placement);

    const pageItems = buildItems('page');
    const metaItems = buildItems('meta');
    const available = buildItems('available');

    return (
        <ConfigCard
            title={tr('cfg_pdi_dtl_title', 'Product description texts per language')}
            description={tr('cfg_pdi_dtl_desc', 'Different languages may have different texts filled in. Pick which description fields to show on the product page, and in what order, for each language.')}
            theme={theme}
        >
            <Tabs
                value={tab}
                onChange={(_e, v) => setTab(v)}
                sx={{
                    minHeight: 36,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: '0.85rem', fontWeight: 600 },
                }}
            >
                {LANGS.map((l) => <Tab key={l.code} label={l.label} />)}
            </Tabs>

            <Stack spacing={2.5} sx={{ mt: 1.5 }}>
                {/* Page texts: ordered list + its own add row beneath */}
                <Box>
                    <SubLabel>{tr('cfg_pdi_dtl_on_page', 'Texts shown on the product page (in order)')}</SubLabel>
                    <ChipsBox>
                        {pageItems.map(it => (
                            <ChipToggle
                                key={it.key}
                                label={tr(it.i18nKey, it.fallback)}
                                onRemove={() => setPlacement(it.key, 'available')}
                            />
                        ))}
                    </ChipsBox>
                    {available.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                            {available.map(it => (
                                <AvailableChip
                                    key={it.key}
                                    label={tr(it.i18nKey, it.fallback)}
                                    onAdd={() => setPlacement(it.key, 'page')}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Meta texts: same available pool, but the + here adds into meta */}
                <Box>
                    <SubLabel>{tr('cfg_pdi_dtl_meta', 'Texts shown for meta & add-on fields (in order)')}</SubLabel>
                    <ChipsBox>
                        {metaItems.map(it => (
                            <ChipToggle
                                key={it.key}
                                label={tr(it.i18nKey, it.fallback)}
                                onRemove={() => setPlacement(it.key, 'available')}
                            />
                        ))}
                    </ChipsBox>
                    {available.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                            {available.map(it => (
                                <AvailableChip
                                    key={`meta-${it.key}`}
                                    label={tr(it.i18nKey, it.fallback)}
                                    onAdd={() => setPlacement(it.key, 'meta')}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Stack>
        </ConfigCard>
    );
};

DescriptionTextsPerLanguageCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default DescriptionTextsPerLanguageCard;
